import React, { ChangeEventHandler, useEffect, useState } from 'react';
import { getCheckboxStyles, getCheckboxLabelColor } from './styleUtils';
import { Check, Minus } from 'react-feather';
import { COLOR_TYPE } from '../globalTypes';
import { Typography } from '../Typography/Typography';
import { GRAY_COLORS } from '../globalTheme';

export type checkboxIconType = 'check' | 'select';
interface CheckboxProps {
	label?: string;
	supportingText?: string;
	checked: boolean;
	onChange: ChangeEventHandler<HTMLInputElement>;
	name: string;
	disabled?: boolean;
	dataTestId?: string;
	type?: COLOR_TYPE;
	iconType?: checkboxIconType;
}

const getCheckboxIcon = (iconType: checkboxIconType, colorType: COLOR_TYPE) => {
	switch (iconType) {
		case 'check':
			return <Check size={12} color={getCheckboxLabelColor(colorType)} />;
		case 'select':
			return <Minus size={14} color={getCheckboxLabelColor(colorType)} />;
	}
};
const Checkbox: React.FC<CheckboxProps> = ({
	label,
	supportingText,
	checked,
	onChange,
	name,
	disabled = false,
	dataTestId,
	type = 'primary',
	iconType = 'check'
}) => {
	const [localChecked, setLocalChecked] = useState(checked);
	const [isFocused, setIsFocused] = useState(false);
	const styles = getCheckboxStyles();

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setLocalChecked(event.target.checked);
		onChange(event);
	};

	const keyDownHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handleChange({
				target: { name, checked: !localChecked }
			} as React.ChangeEvent<HTMLInputElement>);
		}
	};

	useEffect(() => {
		setLocalChecked(checked);
	}, [checked]);

	return (
		<div
			style={styles.container}
			onFocus={() => setIsFocused(true)}
			onBlur={() => setIsFocused(false)}
			onKeyDown={keyDownHandler}
			tabIndex={disabled ? -1 : 0} // Allow the div to be focusable, skip focus if disabled
		>
			<div style={styles.fakeCheckboxContainer}>
				<div
					style={styles.fakeCheckbox(isFocused, type, localChecked, disabled)}
					onClick={(event) => {
						event.preventDefault();
						handleChange({
							target: { name, checked: !localChecked }
						} as React.ChangeEvent<HTMLInputElement>);
					}}
				>
					{localChecked && getCheckboxIcon(iconType, type)}
				</div>
				<input
					data-testid={dataTestId}
					type="checkbox"
					id={name}
					name={name}
					checked={localChecked}
					onChange={handleChange}
					disabled={disabled}
					style={styles.realCheckbox}
					width={16}
					height={16}
					tabIndex={-1}
				/>
			</div>
			<div style={styles.textContainer}>
				<label htmlFor={name}>
					<Typography
						size="sm"
						weight="medium"
						color={disabled ? GRAY_COLORS.gray300 : GRAY_COLORS.gray700}
					>
						{label || ''}
					</Typography>
				</label>
				{supportingText && (
					<Typography
						size="sm"
						weight="regular"
						color={disabled ? GRAY_COLORS.gray300 : GRAY_COLORS.gray600}
					>
						{supportingText}
					</Typography>
				)}
			</div>
		</div>
	);
};

export default Checkbox;
